import { Alert, Button, Col, Divider, Form, Input, InputNumber, Row, Select, Slider, Space, Switch, Upload } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { PAGE_SIZES, syncLanguages } from '../model'

const TWIP_PER_CM = 567

const toCm = (twip) => Math.round(((twip || 0) / TWIP_PER_CM) * 100) / 100
const toTwip = (cm) => Math.round((cm || 0) * TWIP_PER_CM)

/**
 * Sayfa çerçevesi ayarları. Kenar boşlukları kullanıcıya **cm** olarak gösterilir; tanımda twip tutulur
 * (OOXML birimi). Varsayılan A4'tür — eski Word şablonları Letter'dı ve bu sapma builder'da tekrarlanmasın.
 */
export default function PageSettings({
    value,
    onChange,
    language = 'tr',
    selectableLanguages = ['tr'],
    onActiveLanguageChange,
    t = (x) => x,
}) {
    const page = value.page || {}
    const margin = page.margin || {}
    const styles = value.styles || {}

    const setPage = (patch) => onChange({ ...value, page: { ...page, ...patch } })
    const setMargin = (patch) => onChange({ ...value, page: { ...page, margin: { ...margin, ...patch } } })
    const setStyles = (patch) => onChange({ ...value, styles: { ...styles, ...patch } })

    const watermark = value.watermark || {
        enabled: false, type: 'text', text: {}, color: 'C0C0C0',
        opacity: 0.5, rotation: 315, widthPt: 468, heightPt: 117,
    }
    const setWatermark = (patch) => onChange({ ...value, watermark: { ...watermark, ...patch } })

    // Dil listesi bir kurulum kararı — sürekli araç çubuğunda durması gerekmiyor, buraya taşındı.
    const handleLanguagesChange = (selected) => {
        const next = selected.length ? selected : [value.defaultLanguage || 'tr']
        onChange(syncLanguages(value, next))
        if (!next.includes(language) && onActiveLanguageChange) onActiveLanguageChange(next[0])
    }

    const readWatermarkImage = (file) => {
        const reader = new FileReader()
        reader.onload = () => setWatermark({ data: String(reader.result).split(',')[1] || '' })
        reader.readAsDataURL(file)
        return false
    }

    return (
        <Form layout="vertical" size="small">
            <Row gutter={8}>
                <Col span={16}>
                    <Form.Item label={t('Diller')} help={t('Seçilen her dil için metin girilir; layout ortaktır.')}>
                        <Select
                            mode="multiple"
                            value={value.languages || []}
                            onChange={handleLanguagesChange}
                            options={selectableLanguages.map((l) => ({ value: l, label: l.toUpperCase() }))}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label={t('Varsayılan Dil')} help={t('Çeviri eksikse bu dile düşülür.')}>
                        <Select
                            value={value.defaultLanguage}
                            onChange={(lang) => onChange({ ...value, defaultLanguage: lang })}
                            options={(value.languages || []).map((l) => ({ value: l, label: l.toUpperCase() }))}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Divider orientation="left" plain style={{ margin: '4px 0 12px' }}>
                {t('Sayfa')}
            </Divider>

            <Row gutter={8}>
                <Col span={12}>
                    <Form.Item label={t('Şablon Adı')}>
                        <Input
                            value={value.name || ''}
                            onChange={(e) => onChange({ ...value, name: e.target.value })}
                            placeholder={t('ör. MR Onam Formu')}
                        />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item label={t('Sayfa')}>
                        <Select
                            value={page.size || 'A4'}
                            onChange={(size) => setPage({ size })}
                            options={PAGE_SIZES.map((s) => ({ value: s, label: s }))}
                        />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item label={t('Yön')}>
                        <Select
                            value={page.orientation || 'portrait'}
                            onChange={(orientation) => setPage({ orientation })}
                            options={[
                                { value: 'portrait', label: t('Dikey') },
                                { value: 'landscape', label: t('Yatay') },
                            ]}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={8}>
                {[
                    ['top', t('Üst')],
                    ['bottom', t('Alt')],
                    ['left', t('Sol')],
                    ['right', t('Sağ')],
                ].map(([key, label]) => (
                    <Col span={6} key={key}>
                        <Form.Item label={`${label} (cm)`}>
                            <InputNumber
                                min={0}
                                max={10}
                                step={0.1}
                                style={{ width: '100%' }}
                                value={toCm(margin[key])}
                                onChange={(cm) => setMargin({ [key]: toTwip(cm) })}
                            />
                        </Form.Item>
                    </Col>
                ))}
            </Row>

            <Row gutter={8}>
                <Col span={12}>
                    <Form.Item label={t('Yazı Tipi')}>
                        <Select
                            value={styles.font || 'Times New Roman'}
                            onChange={(font) => setStyles({ font })}
                            options={['Times New Roman', 'Arial', 'Calibri', 'Verdana', 'Tahoma'].map((f) => ({
                                value: f,
                                label: f,
                            }))}
                        />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item label={t('Punto')}>
                        <InputNumber
                            min={6}
                            max={24}
                            step={0.5}
                            style={{ width: '100%' }}
                            value={styles.size || 10}
                            onChange={(size) => setStyles({ size })}
                        />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item label={t('Satır Aralığı')}>
                        <InputNumber
                            min={0.8}
                            max={3}
                            step={0.05}
                            style={{ width: '100%' }}
                            value={styles.lineHeight || 1.15}
                            onChange={(lineHeight) => setStyles({ lineHeight })}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Divider orientation="left" plain style={{ margin: '4px 0 12px' }}>
                {t('Filigran')}
            </Divider>

            <Row gutter={8} align="middle">
                <Col>
                    <Form.Item label={t('Filigran')} style={{ marginBottom: 8 }}>
                        <Switch
                            checked={!!watermark.enabled}
                            onChange={(enabled) => setWatermark({ enabled })}
                        />
                    </Form.Item>
                </Col>
                {watermark.enabled && (
                    <Col span={6}>
                        <Form.Item label={t('Tür')} style={{ marginBottom: 8 }}>
                            <Select
                                value={watermark.type || 'text'}
                                onChange={(type) => setWatermark({ type })}
                                options={[
                                    { value: 'text', label: t('Metin') },
                                    { value: 'image', label: t('Görsel') },
                                ]}
                            />
                        </Form.Item>
                    </Col>
                )}
            </Row>

            {watermark.enabled && (
                <>
                    {watermark.type === 'image' ? (
                        <Form.Item label={t('Filigran Görseli')} style={{ marginBottom: 8 }}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Upload accept="image/png,image/jpeg" showUploadList={false} beforeUpload={readWatermarkImage}>
                                    <Button size="small" icon={<UploadOutlined />}>{t('Görsel Yükle')}</Button>
                                </Upload>
                                {watermark.data ? (
                                    <img
                                        src={`data:image/png;base64,${watermark.data}`}
                                        alt=""
                                        style={{ maxHeight: 80, border: '1px solid #eee' }}
                                    />
                                ) : (
                                    <Alert type="info" showIcon message={t('Henüz görsel yüklenmedi')} />
                                )}
                            </Space>
                        </Form.Item>
                    ) : (
                        <Row gutter={8}>
                            <Col span={16}>
                                <Form.Item label={`${t('Filigran Metni')} (${language.toUpperCase()})`} style={{ marginBottom: 8 }}>
                                    <Input
                                        value={(watermark.text || {})[language] || ''}
                                        onChange={(e) =>
                                            setWatermark({ text: { ...(watermark.text || {}), [language]: e.target.value } })
                                        }
                                        placeholder={t('ör. ÖRNEKTİR')}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item label={t('Renk')} style={{ marginBottom: 8 }}>
                                    <Input
                                        addonBefore="#"
                                        maxLength={6}
                                        value={watermark.color || 'C0C0C0'}
                                        onChange={(e) => setWatermark({ color: e.target.value.replace(/[^0-9a-fA-F]/g, '') })}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    <Row gutter={8}>
                        <Col span={8}>
                            <Form.Item label={t('Saydamlık')} style={{ marginBottom: 8 }}>
                                <Slider
                                    min={0.05}
                                    max={1}
                                    step={0.05}
                                    value={watermark.opacity ?? 0.5}
                                    onChange={(opacity) => setWatermark({ opacity })}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={5}>
                            <Form.Item label={t('Açı')} style={{ marginBottom: 8 }}>
                                <Select
                                    value={watermark.rotation ?? 315}
                                    onChange={(rotation) => setWatermark({ rotation })}
                                    options={[
                                        { value: 0, label: t('Yatay') },
                                        { value: 315, label: t('Çapraz (yukarı)') },
                                        { value: 45, label: t('Çapraz (aşağı)') },
                                        { value: 270, label: t('Dikey') },
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={5}>
                            <Form.Item label={t('Genişlik (pt)')} style={{ marginBottom: 8 }}>
                                <InputNumber
                                    min={20}
                                    max={2000}
                                    style={{ width: '100%' }}
                                    value={watermark.widthPt ?? 468}
                                    onChange={(widthPt) => setWatermark({ widthPt })}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label={t('Yükseklik (pt)')} style={{ marginBottom: 8 }}>
                                <InputNumber
                                    min={10}
                                    max={2000}
                                    style={{ width: '100%' }}
                                    value={watermark.heightPt ?? 117}
                                    onChange={(heightPt) => setWatermark({ heightPt })}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Alert
                        type="info"
                        showIcon
                        message={t('Filigran her sayfada metnin arkasında görünür. Metin filigranında {{...}} alanları kullanılabilir.')}
                    />
                </>
            )}
        </Form>
    )
}
